import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ChallengeReview from '../components/ChallengeReview';
import {
  Trophy,
  Timer,
  Lightning,
  Check,
  X,
  Play,
  Users,
  UsersThree,
  MagnifyingGlass,
  UserPlus,
  Star,
  Crown,
  SpinnerGap,
  ArrowRight
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChallengePage = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [buddies, setBuddies] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuddy, setSelectedBuddy] = useState(null);
  const [challengeType, setChallengeType] = useState('vocabulary');
  const [difficulty, setDifficulty] = useState('medium');
  const [creating, setCreating] = useState(false);

  // Active challenge state
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ── Freunde (top section) ────────────────────────────────────────────────
  // Privacy-first: students must TYPE a name (≥3 chars) — we never display
  // the full student roster. /buddies/suggestions returns [] without a query,
  // and only name-matches with at least 3 chars. The full management UI for
  // existing buddies still lives at /buddies.
  const [friendQuery, setFriendQuery] = useState('');
  const [friendSuggestions, setFriendSuggestions] = useState([]);
  const [pendingInvites, setPendingInvites] = useState({});
  const [friendSearching, setFriendSearching] = useState(false);

  useEffect(() => {
    const q = friendQuery.trim();
    if (q.length < 3) {
      setFriendSuggestions([]);
      setFriendSearching(false);
      return;
    }
    setFriendSearching(true);
    const t = setTimeout(() => {
      axios.get(`${API}/buddies/suggestions?q=${encodeURIComponent(q)}`, { withCredentials: true })
        .then(r => setFriendSuggestions(r.data || []))
        .catch(() => setFriendSuggestions([]))
        .finally(() => setFriendSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [friendQuery]);

  const matchesQuery = (name) => {
    const q = friendQuery.trim().toLowerCase();
    if (!q) return true;
    return (name || '').toLowerCase().includes(q);
  };

  const inviteFriend = async (targetUserId) => {
    if (pendingInvites[targetUserId]) return;
    setPendingInvites(p => ({ ...p, [targetUserId]: true }));
    try {
      await axios.post(`${API}/buddies/request`, {
        target_user_id: targetUserId,
        message: '¡Hola! Me gustaría practicar español contigo.',
      }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Solicitud enviada' : 'Anfrage gesendet');
      setFriendSuggestions(prev => prev.filter(s => s.user_id !== targetUserId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      setPendingInvites(p => { const n = { ...p }; delete n[targetUserId]; return n; });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let interval;
    if (activeChallenge && startTime && !submitted) {
      interval = setInterval(() => {
        setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeChallenge, startTime, submitted]);

  const loadData = async () => {
    try {
      const [buddiesRes, challengesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/buddies/my-buddies`, { withCredentials: true }),
        axios.get(`${API}/challenges/active`, { withCredentials: true }),
        axios.get(`${API}/challenges/pending`, { withCredentials: true })
      ]);
      
      setBuddies(buddiesRes.data);
      setChallenges(challengesRes.data);
      setPendingChallenges(pendingRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createChallenge = async () => {
    if (!selectedBuddy) {
      toast.error('Wähle einen Buddy aus');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/challenges/create`, {
        buddy_connection_id: selectedBuddy.connection_id,
        challenge_type: challengeType,
        difficulty: difficulty
      }, { withCredentials: true });
      
      toast.success('Challenge erstellt! Dein Buddy wurde benachrichtigt.');
      loadData();
      setSelectedBuddy(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const acceptChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/accept`, {}, { withCredentials: true });
      toast.success('Challenge angenommen!');
      loadData();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const startChallenge = async (challenge) => {
    try {
      const res = await axios.get(`${API}/challenges/${challenge.challenge_id}`, { withCredentials: true });
      setActiveChallenge(res.data);
      setCurrentQuestion(0);
      setAnswers([]);
      setStartTime(Date.now());
      setTimeElapsed(0);
      setShowResults(false);
      setSubmitted(false);
    } catch (error) {
      toast.error('Fehler beim Laden der Challenge');
    }
  };

  const selectAnswer = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < activeChallenge.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const [lastReview, setLastReview] = useState(null);
  const [lastScore, setLastScore] = useState(null);

  const submitChallenge = async () => {
    if (answers.length < activeChallenge.questions.length) {
      toast.error('Bitte beantworte alle Fragen');
      return;
    }

    setSubmitted(true);
    try {
      const res = await axios.post(`${API}/challenges/${activeChallenge.challenge_id}/submit`, {
        answers: answers,
        time_taken: timeElapsed
      }, { withCredentials: true });
      
      toast.success(`Eingereicht! ${res.data.score}/${res.data.total} richtig`);
      setLastReview(res.data.review || null);
      setLastScore({ score: res.data.score, total: res.data.total });
      setShowResults(true);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Einreichen');
      setSubmitted(false);
    }
  };

  const closeChallenge = () => {
    setActiveChallenge(null);
    setShowResults(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (challenge) => {
    if (challenge.status === 'completed') {
      if (challenge.winner_id === user?.user_id) return 'text-green-600 bg-green-50';
      if (challenge.winner_id === null) return 'text-amber-600 bg-amber-50';
      return 'text-red-600 bg-red-50';
    }
    if (challenge.status === 'active') return 'text-blue-600 bg-blue-50';
    return 'text-[#78716C] bg-[#F5F5F4]';
  };

  const getStatusText = (challenge) => {
    if (challenge.status === 'completed') {
      if (challenge.winner_id === user?.user_id) return 'Gewonnen!';
      if (challenge.winner_id === null) return 'Unentschieden';
      return 'Verloren';
    }
    if (challenge.status === 'active') return 'Aktiv';
    return 'Ausstehend';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SpinnerGap size={40} className="animate-spin text-[#AB1C24]" />
      </div>
    );
  }

  // Active Challenge View
  if (activeChallenge && !showResults) {
    const question = activeChallenge.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / activeChallenge.questions.length) * 100;

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="challenge-active">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Lightning size={24} className="text-amber-500" weight="fill" />
              <span className="font-heading font-bold text-lg">
                {activeChallenge.challenge_type === 'vocabulary' ? 'Vokabel' : 'Grammatik'}-Challenge
              </span>
            </div>
            <div className="flex items-center gap-2 bg-[#F5F5F4] px-4 py-2 rounded-xl">
              <Timer size={20} className="text-[#AB1C24]" />
              <span className="font-mono font-bold">{formatTime(timeElapsed)}</span>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-[#78716C] mt-2">
            Frage {currentQuestion + 1} von {activeChallenge.questions.length}
          </p>
        </div>

        {/* Question */}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
          <h2 className="font-heading text-xl font-bold text-[#292524] mb-6 text-center">
            {question.question}
          </h2>

          <div className="grid gap-3">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => selectAnswer(option)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  answers[currentQuestion] === option
                    ? 'border-[#AB1C24] bg-[#AB1C24]/5'
                    : 'border-[#D6D3D1] hover:border-[#AB1C24]/50'
                }`}
                data-testid={`option-${idx}`}
              >
                <span className="font-medium">{option}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
            className="flex-1"
          >
            Zurück
          </Button>
          
          {currentQuestion < activeChallenge.questions.length - 1 ? (
            <Button
              onClick={nextQuestion}
              disabled={!answers[currentQuestion]}
              className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C]"
            >
              Weiter
              <ArrowRight size={20} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submitChallenge}
              disabled={submitted || answers.length < activeChallenge.questions.length}
              className="flex-1 bg-[#6B8E23] hover:bg-[#5a7a1e]"
              data-testid="submit-challenge-btn"
            >
              {submitted ? <SpinnerGap size={20} className="animate-spin mr-2" /> : null}
              Abschicken
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Results View
  if (showResults && activeChallenge) {
    const isChallenger = activeChallenge.challenger_id === user?.user_id;
    // Prefer the fresh score returned by /submit — `activeChallenge.*_score`
    // is the pre-submit DB snapshot (always 0 until completed). Falling back
    // to it covers the case where the user reopens a completed challenge.
    const myScore = lastScore?.score ?? (isChallenger ? activeChallenge.challenger_score : activeChallenge.opponent_score);
    const myTotal = lastScore?.total ?? activeChallenge.questions.length;

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="challenge-results">
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
            <Trophy size={40} className="text-white" weight="fill" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#292524] mb-2">
            Challenge abgeschlossen!
          </h2>
          <p className="text-[#57534E] mb-6">
            Du hast {myScore} von {myTotal} Fragen richtig beantwortet in {formatTime(timeElapsed)}.
          </p>

          <div className="bg-[#F5F5F4] rounded-xl p-4 mb-6">
            <p className="text-sm text-[#78716C]">Warte auf deinen Buddy oder überprüfe später das Ergebnis.</p>
          </div>

          <Button onClick={closeChallenge} className="bg-[#AB1C24] hover:bg-[#8A151C]">
            Zurück zur Übersicht
          </Button>
        </div>
        {lastReview && lastReview.length > 0 && (
          <div className="text-left">
            <ChallengeReview review={lastReview} lang={lang} />
          </div>
        )}
      </div>
    );
  }

  // Main View
  return (
    <div className="space-y-6" data-testid="challenge-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
          <Lightning size={28} weight="duotone" className="text-[#E06D53]" />
          {lang === 'es' ? 'Desafíos' : 'Challenge'}
        </h1>
        <p className="text-[#57534E] mt-1">{lang === 'es' ? '¡Reta a tus amigos a duelos de conocimiento!' : 'Fordere deine Buddies zu Wissens-Duellen heraus!'}</p>
      </div>

      {/* ── Freunde panel (search + 1-click invites) ──────────────────────── */}
      <section
        className="bg-white rounded-2xl border border-[#D6D3D1] p-4 sm:p-5 space-y-3"
        data-testid="friends-panel"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading font-semibold text-lg text-[#292524] flex items-center gap-2">
            <UsersThree size={20} weight="duotone" className="text-[#2F6B50]" />
            {lang === 'es' ? 'Amigos' : 'Freunde'}
            <span className="text-xs font-normal text-[#A8A29E]">({buddies.length})</span>
          </h2>
          <Link
            to="/buddies"
            className="text-xs font-semibold text-[#AB1C24] hover:underline"
            data-testid="friends-manage-link"
          >
            {lang === 'es' ? 'Gestionar todos →' : 'Alle verwalten →'}
          </Link>
        </div>

        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <input
            type="text"
            value={friendQuery}
            onChange={e => setFriendQuery(e.target.value)}
            placeholder={lang === 'es' ? 'Escribe el nombre completo (mín. 3 letras)…' : 'Vollständigen Namen tippen (mind. 3 Buchstaben)…'}
            className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#AB1C24]"
            data-testid="friends-search"
          />
        </div>

        {friendQuery.trim() && friendQuery.trim().length < 3 && (
          <p className="text-[11px] text-[#A8A29E] italic">
            {lang === 'es' ? 'Mínimo 3 letras para buscar — protegemos la privacidad de los alumnos.' : 'Mind. 3 Buchstaben — wir schützen die Privatsphäre der Mitlernenden.'}
          </p>
        )}

        {buddies.filter(b => matchesQuery(b.name)).length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#A8A29E] font-bold mb-1.5">
              {lang === 'es' ? 'Tus amigos' : 'Deine Buddies'}
            </p>
            <div className="flex flex-wrap gap-2">
              {buddies.filter(b => matchesQuery(b.name)).slice(0, 12).map(b => (
                <button
                  key={b.user_id}
                  onClick={() => setSelectedBuddy(b)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    selectedBuddy?.user_id === b.user_id
                      ? 'bg-[#AB1C24] text-white border-[#AB1C24]'
                      : 'bg-white border-stone-200 text-[#292524] hover:border-[#AB1C24]'
                  }`}
                  data-testid={`friend-pill-${b.user_id}`}
                >
                  <Users size={12} weight="fill" />
                  {b.name}
                  <span className="text-[10px] opacity-70">· {b.spanish_level}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {friendQuery.trim().length >= 3 && (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#A8A29E] font-bold mb-1.5 mt-2">
              {lang === 'es' ? 'Resultados' : 'Treffer'}
            </p>
            {friendSearching ? (
              <p className="text-xs text-[#A8A29E] italic">{lang === 'es' ? 'Buscando…' : 'Suche läuft…'}</p>
            ) : friendSuggestions.length === 0 ? (
              <p className="text-xs text-[#A8A29E] italic">{lang === 'es' ? 'Sin coincidencias.' : 'Keine Treffer.'}</p>
            ) : (
              <div className="space-y-1.5">
                {friendSuggestions.slice(0, 10).map(s => (
                  <div
                    key={s.user_id}
                    className="flex items-center justify-between gap-2 bg-stone-50 rounded-lg p-2"
                    data-testid={`friend-suggestion-${s.user_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#292524] truncate">{s.name}</p>
                      <p className="text-[11px] text-[#A8A29E]">{s.spanish_level || 'A2'}</p>
                    </div>
                    <button
                      onClick={() => inviteFriend(s.user_id)}
                      disabled={!!pendingInvites[s.user_id]}
                      className="inline-flex items-center gap-1 bg-[#2F6B50] hover:bg-[#245140] disabled:bg-stone-300 text-white px-2.5 py-1 rounded text-xs font-semibold"
                      data-testid={`friend-invite-${s.user_id}`}
                    >
                      <UserPlus size={12} weight="bold" />
                      {lang === 'es' ? 'Invitar' : 'Anfragen'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {buddies.length === 0 && !friendQuery && (
          <p className="text-sm text-[#A8A29E] italic">
            {lang === 'es'
              ? 'Aún no tienes amigos. Busca por nombre o gestiona desde "Amigos".'
              : 'Noch keine Buddies. Such nach Namen oder verwalte sie unter "Freunde".'}
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create Challenge */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            <h2 className="font-heading font-semibold text-lg text-[#292524] mb-4 flex items-center gap-2">
              <Lightning size={24} className="text-amber-500" weight="fill" />
              Neue Challenge
            </h2>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#57534E] mb-2 block">Buddy auswählen</label>
                <Select value={selectedBuddy?.user_id || ''} onValueChange={(v) => setSelectedBuddy(buddies.find(b => b.user_id === v))}>
                  <SelectTrigger data-testid="buddy-select">
                    <SelectValue placeholder="Wähle einen Buddy" />
                  </SelectTrigger>
                  <SelectContent>
                    {buddies.map((buddy) => (
                      <SelectItem key={buddy.user_id} value={buddy.user_id}>
                        {buddy.name} ({buddy.spanish_level})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-[#57534E] mb-2 block">Challenge-Typ</label>
                <Select value={challengeType} onValueChange={setChallengeType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vocabulary">Vokabeln</SelectItem>
                    <SelectItem value="grammar">Grammatik</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-[#57534E] mb-2 block">Schwierigkeit</label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Einfach</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="hard">Schwer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={createChallenge}
                disabled={creating || !selectedBuddy}
                className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
                data-testid="create-challenge-btn"
              >
                {creating ? <SpinnerGap size={20} className="animate-spin mr-2" /> : <Lightning size={20} className="mr-2" />}
                Challenge starten
              </Button>
            </div>

            {buddies.length === 0 && (
              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-700">
                  Du brauchst mindestens einen Buddy für Challenges. Gehe zur Buddies-Seite um Lernpartner zu finden!
                </p>
              </div>
            )}
          </div>

          {/* Pending Challenges */}
          {pendingChallenges.length > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
              <h3 className="font-heading font-semibold text-lg text-amber-800 mb-4">
                Eingehende Challenges ({pendingChallenges.length})
              </h3>
              <div className="space-y-3">
                {pendingChallenges.map((challenge) => (
                  <div key={challenge.challenge_id} className="bg-white rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{challenge.challenger_name}</p>
                        <p className="text-sm text-[#78716C]">
                          {challenge.challenge_type === 'vocabulary' ? 'Vokabeln' : 'Grammatik'} • {challenge.difficulty}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => acceptChallenge(challenge.challenge_id)}
                        className="bg-[#6B8E23] hover:bg-[#5a7a1e]"
                      >
                        Annehmen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Challenge History */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            <h2 className="font-heading font-semibold text-lg text-[#292524] mb-4 flex items-center gap-2">
              <Trophy size={24} className="text-amber-500" weight="fill" />
              Deine Challenges
            </h2>

            {challenges.length > 0 ? (
              <div className="space-y-3">
                {challenges.map((challenge) => {
                  const isChallenger = challenge.challenger_id === user?.user_id;
                  const opponentName = isChallenger ? challenge.opponent_name : challenge.challenger_name;
                  const myScore = isChallenger ? challenge.challenger_score : challenge.opponent_score;
                  const oppScore = isChallenger ? challenge.opponent_score : challenge.challenger_score;
                  const hasSubmitted = isChallenger 
                    ? challenge.challenger_answers?.length > 0 
                    : challenge.opponent_answers?.length > 0;

                  return (
                    <div 
                      key={challenge.challenge_id}
                      className="flex items-center justify-between p-4 rounded-xl border border-[#D6D3D1] hover:border-[#AB1C24]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#AB1C24] to-[#5B8C9A] flex items-center justify-center text-white font-bold">
                          {opponentName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[#292524]">vs. {opponentName}</p>
                          <p className="text-sm text-[#78716C]">
                            {challenge.challenge_type === 'vocabulary' ? 'Vokabeln' : 'Grammatik'} • {challenge.difficulty}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {challenge.status === 'completed' && (
                          <div className="text-center">
                            <p className="text-lg font-bold">{myScore} : {oppScore}</p>
                          </div>
                        )}
                        
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(challenge)}`}>
                          {getStatusText(challenge)}
                        </span>

                        {(challenge.status === 'active' || (challenge.status === 'pending' && !isChallenger)) && !hasSubmitted && (
                          <Button
                            size="sm"
                            onClick={() => startChallenge(challenge)}
                            className="bg-[#AB1C24] hover:bg-[#8A151C]"
                          >
                            <Play size={16} className="mr-1" />
                            Spielen
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto text-[#78716C]/30 mb-4" />
                <p className="text-[#78716C]">Noch keine Challenges</p>
                <p className="text-sm text-[#78716C]">Erstelle eine Challenge um loszulegen!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengePage;
